import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield,
  MoreVertical,
  Edit2,
  Trash2,
  Eye,
  User,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { AdminClientApi, ClientBelief, GetBeliefsInputInterface } from '../Admin-ClientLibrary';
import { getApiUrl } from '../config/api';
import './BeliefsListView.css';

// Initialize the Admin Client Library
const getToken = (): string => {
  return localStorage.getItem('authToken') || '';
};

const adminApi = new AdminClientApi(getApiUrl(), getToken);

interface BeliefsListViewProps {
  filters?: {
    position?: string;
    userId?: number;
    goeUserId?: number;
    loeUserId?: number;
    thoughtId?: number;
    createdAfter?: string;
    createdBefore?: string;
    supportingBeliefsFor?: number;
  };
  showCheckboxes?: boolean;
  showBulkActions?: boolean;
  title?: string;
  compact?: boolean;
  limit?: number;
}

const BeliefsListView: React.FC<BeliefsListViewProps> = ({
  filters = {},
  showCheckboxes = true,
  showBulkActions = true,
  title,
  compact = false,
  limit,
}) => {
  const navigate = useNavigate();

  const [beliefs, setBeliefs] = useState<ClientBelief[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedBeliefs, setSelectedBeliefs] = useState<Set<number>>(new Set());
  const [totalBeliefs, setTotalBeliefs] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Page history stack for cursor-based pagination
  const [pageHistory, setPageHistory] = useState<Array<{ cursor: string | null; pageNum: number }>>(
    [{ cursor: null, pageNum: 1 }]
  );

  // Ordering state
  const [orderBy, setOrderBy] = useState<string>('id');
  const [orderDirection, setOrderDirection] = useState<string>('DESC');

  const itemsPerPage = limit || (compact ? 10 : 20);

  // Fetch beliefs when component mounts or when filters/ordering changes
  useEffect(() => {
    fetchBeliefs('first', undefined, true);
  }, [filters, orderBy, orderDirection]);

  // Function to fetch beliefs from API with cursor-based pagination
  const fetchBeliefs = async (
    direction: 'first' | 'next' | 'previous' = 'first',
    cursor?: string,
    shouldGetCount: boolean = false
  ): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const apiParams: GetBeliefsInputInterface = {
        limit: itemsPerPage,
        orderBy,
        orderDirection,
        getCount: shouldGetCount,
      };

      // Add cursor parameters based on direction and order
      if (direction === 'next' && cursor) {
        if (orderBy === 'id') {
          if (orderDirection === 'DESC') {
            apiParams.beforeId = parseInt(cursor);
          } else {
            apiParams.afterId = parseInt(cursor);
          }
        } else if (orderBy === 'createdAt') {
          if (orderDirection === 'DESC') {
            apiParams.beforeCreatedDate = cursor;
          } else {
            apiParams.afterCreatedDate = cursor;
          }
        }
      } else if (direction === 'previous' && cursor) {
        if (orderBy === 'id') {
          if (orderDirection === 'DESC') {
            apiParams.afterId = parseInt(cursor);
          } else {
            apiParams.beforeId = parseInt(cursor);
          }
        } else if (orderBy === 'createdAt') {
          if (orderDirection === 'DESC') {
            apiParams.afterCreatedDate = cursor;
          } else {
            apiParams.beforeCreatedDate = cursor;
          }
        }
      }

      // Add filter parameters from props
      if (filters.position) {
        apiParams.position = filters.position;
      }
      if (filters.userId) {
        apiParams.userId = filters.userId;
      }
      if (filters.goeUserId) {
        apiParams.goeUserId = filters.goeUserId;
      }
      if (filters.loeUserId) {
        apiParams.loeUserId = filters.loeUserId;
      }
      if (filters.thoughtId) {
        apiParams.thoughtId = filters.thoughtId;
      }
      if (filters.createdAfter) {
        apiParams.createdAfter = filters.createdAfter;
      }
      if (filters.createdBefore) {
        apiParams.createdBefore = filters.createdBefore;
      }
      if (filters.supportingBeliefsFor) {
        apiParams.supportingBeliefsFor = filters.supportingBeliefsFor;
      }

      const response = await adminApi.getBeliefs(apiParams);

      // Check if no results and show alert for navigation attempts
      if (response.beliefs.length === 0 && (direction === 'next' || direction === 'previous')) {
        alert('No results');
        return;
      }

      setBeliefs(response.beliefs);

      // Only update total count if it was requested and returned
      if (shouldGetCount && response.total !== undefined) {
        setTotalBeliefs(response.total);
      }

      // Update page history and current page based on direction
      if (direction === 'first') {
        // Reset to first page
        setCurrentPage(1);
        setPageHistory([{ cursor: null, pageNum: 1 }]);
      } else if (direction === 'next' && response.beliefs.length > 0) {
        // Add current page to history and move to next page
        const nextPageNum = currentPage + 1;
        const lastItem = response.beliefs[response.beliefs.length - 1];
        const nextCursor =
          orderBy === 'id' ? lastItem.id.toString() : lastItem.createdAt.toISOString();

        setCurrentPage(nextPageNum);
        setPageHistory((prev) => [...prev, { cursor: nextCursor, pageNum: nextPageNum }]);
      } else if (direction === 'previous') {
        // Move back in history
        setPageHistory((prev) => {
          const newHistory = [...prev];
          newHistory.pop(); // Remove current page
          const previousPage = newHistory[newHistory.length - 1];
          setCurrentPage(previousPage.pageNum);
          return newHistory;
        });
      }
    } catch (err: any) {
      console.error('Error fetching beliefs:', err);
      setError(err.message || 'Failed to fetch beliefs');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectBelief = (beliefId: number): void => {
    setSelectedBeliefs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(beliefId)) {
        newSet.delete(beliefId);
      } else {
        newSet.add(beliefId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (): void => {
    if (selectedBeliefs.size === beliefs.length && beliefs.length > 0) {
      setSelectedBeliefs(new Set());
    } else {
      setSelectedBeliefs(new Set(beliefs.map((belief) => belief.id)));
    }
  };

  const getPositionColor = (position: string): string => {
    switch (position) {
      case 'believe':
        return 'believe';
      case 'disbelieve':
        return 'disbelieve';
      case 'unsure':
        return 'unsure';
      default:
        return 'unsure';
    }
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString();
  };

  const handleNextPage = (): void => {
    if (beliefs.length === itemsPerPage) {
      const lastItem = beliefs[beliefs.length - 1];
      const cursor = orderBy === 'id' ? lastItem.id.toString() : lastItem.createdAt.toISOString();
      fetchBeliefs('next', cursor);
    }
  };

  const handlePreviousPage = (): void => {
    if (currentPage > 1) {
      const previousPage = pageHistory[pageHistory.length - 2];
      fetchBeliefs('previous', previousPage?.cursor || undefined);
    }
  };

  const handleOrderByChange = (newOrderBy: string): void => {
    setOrderBy(newOrderBy);
    setCurrentPage(1);
    setPageHistory([{ cursor: null, pageNum: 1 }]);
  };

  const handleOrderDirectionChange = (newDirection: string): void => {
    setOrderDirection(newDirection);
    setCurrentPage(1);
    setPageHistory([{ cursor: null, pageNum: 1 }]);
  };

  const handleRowClick = (beliefId: number, event: React.MouseEvent): void => {
    // Don't navigate if clicking on checkbox or action buttons
    const target = event.target as HTMLElement;
    if (
      (target as HTMLInputElement).type === 'checkbox' ||
      target.closest('.action-btn') ||
      target.closest('.user-link')
    ) {
      return;
    }

    // Navigate to belief detail view
    const belief = beliefs.find((b) => b.id === beliefId);
    if (belief) {
      navigate(`/users/${belief.user_id}/belief/${beliefId}`);
    }
  };

  // Calculate total pages estimate
  const totalPages = Math.max(1, Math.ceil(totalBeliefs / itemsPerPage));

  return (
    <div className={`beliefs-list-view ${compact ? 'compact' : ''}`}>
      {title && (
        <div className="list-view-header">
          <h3>{title}</h3>
        </div>
      )}

      <div className="beliefs-controls">
        <div className="controls-row">
          {/* Total count */}
          <div className="beliefs-stats">
            <span className="stat-item">
              <Shield size={16} />
              {totalBeliefs} Total Beliefs
            </span>
          </div>

          {/* Order By Controls */}
          <div className="order-controls">
            <div className="order-group">
              <label>Order by:</label>
              <select
                value={orderBy}
                onChange={(e) => handleOrderByChange(e.target.value)}
                className="order-select"
              >
                <option value="id">ID</option>
                <option value="createdAt">Created Date</option>
              </select>
            </div>
            <div className="order-group">
              <label>Direction:</label>
              <select
                value={orderDirection}
                onChange={(e) => handleOrderDirectionChange(e.target.value)}
                className="order-select"
              >
                <option value="DESC">Descending</option>
                <option value="ASC">Ascending</option>
              </select>
            </div>
          </div>
        </div>

        {showBulkActions && selectedBeliefs.size > 0 && (
          <div className="bulk-actions">
            <span>{selectedBeliefs.size} selected</span>
            <button className="btn-secondary">
              <Edit2 size={14} />
              Edit
            </button>
            <button className="btn-danger">
              <Trash2 size={14} />
              Delete
            </button>
          </div>
        )}
      </div>

      <div className="beliefs-content">
        {error && (
          <div className="error-message">
            <p>Error: {error}</p>
          </div>
        )}

        {loading ? (
          <div className="loading">Loading beliefs...</div>
        ) : (
          <>
            <div className="beliefs-table-container">
              <table className="beliefs-table">
                <thead>
                  <tr>
                    {showCheckboxes && (
                      <th>
                        <input
                          type="checkbox"
                          checked={selectedBeliefs.size === beliefs.length && beliefs.length > 0}
                          onChange={handleSelectAll}
                        />
                      </th>
                    )}
                    <th>Belief ID</th>
                    <th>Position</th>
                    <th>User ID</th>
                    <th>Thought Text</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {beliefs.map((belief) => (
                    <tr
                      key={belief.id}
                      onClick={(e) => handleRowClick(belief.id, e)}
                      className="belief-row"
                    >
                      {showCheckboxes && (
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedBeliefs.has(belief.id)}
                            onChange={() => handleSelectBelief(belief.id)}
                          />
                        </td>
                      )}
                      <td>
                        <span className="belief-id">{belief.id}</span>
                      </td>
                      <td>
                        <span className={`position-badge ${getPositionColor(belief.position)}`}>
                          {belief.position.toUpperCase()}
                        </span>
                      </td>
                      <td>
                        <button
                          className="user-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/users/${belief.user_id}`);
                          }}
                        >
                          <User size={14} />
                          {belief.user_id}
                        </button>
                      </td>
                      <td>
                        <div className="thought-text">
                          {belief.thought?.text || 'No thought text available'}
                        </div>
                      </td>
                      <td>{formatDate(belief.createdAt)}</td>
                      <td>
                        <div className="actions">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/users/${belief.user_id}/belief/${belief.id}`);
                            }}
                            className="action-btn"
                            title="Manage Belief"
                          >
                            <Shield size={14} />
                          </button>
                          <button className="action-btn" title="View Details">
                            <Eye size={14} />
                          </button>
                          <button className="action-btn" title="More Options">
                            <MoreVertical size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <div className="pagination-info">Showing {beliefs.length} beliefs</div>
              <div className="pagination-controls">
                <button
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  className="btn-secondary"
                >
                  Previous
                </button>
                <span className="page-info">
                  Page {currentPage} of {totalPages}
                </span>
                <button onClick={handleNextPage} className="btn-secondary">
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BeliefsListView;
